import React, { useEffect, useId, useRef, useState } from 'react';
import { Camera, FileText, Loader2, RefreshCw, Trash2, Upload, CheckCircle2 } from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';

const DEFAULT_ACCEPT = 'image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf';
const MAX_BYTES = 10 * 1024 * 1024;

const describeFileError = (file) => {
  if (!file) return 'No file selected';
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif')) {
    return 'HEIC photos are not supported. On iPhone, go to Settings › Camera › Formats and choose "Most Compatible", or pick a JPEG.';
  }
  const okType = ['image/jpeg', 'image/png', 'application/pdf'].includes(type)
    || /\.(jpe?g|png|pdf)$/.test(name);
  if (!okType) return 'Please upload a JPG, PNG, or PDF file.';
  if (file.size > MAX_BYTES) return 'That file is larger than 10 MB. Try a smaller photo or a compressed PDF.';
  return null;
};

/**
 * One document slot (e.g. "License front").
 *
 * Props:
 *   label, hint, required, accept
 *   existing   { id, originalFileName, mimeType, previewUrl } | null
 *   onUpload   async (file, onProgress(0-100)) => void
 *   onRemove   async (existing) => void
 *   disabled, error (external validation message)
 *   fieldName  form error key (e.g. 'documents.license_front') so ErrorSummary links can focus the slot
 */
const FileSlot = ({
  label,
  hint,
  required = false,
  accept = DEFAULT_ACCEPT,
  existing = null,
  onUpload,
  onRemove,
  disabled = false,
  error: externalError,
  capture = 'environment',
  fieldName,
  className = ''
}) => {
  const id = useId();
  const isMobile = useIsMobile();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localError, setLocalError] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);

  useEffect(() => () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  const handleFile = async (file) => {
    const problem = describeFileError(file);
    if (problem) {
      setLocalError(problem);
      return;
    }
    setLocalError(null);
    if (file.type.startsWith('image/')) {
      setLocalPreview(URL.createObjectURL(file));
    } else {
      setLocalPreview(null);
    }
    setUploading(true);
    setProgress(0);
    try {
      await onUpload(file, (pct) => setProgress(pct));
    } catch (err) {
      setLocalError(err?.getUserFriendlyMessage?.() || err?.message || 'Upload failed. Please try again.');
      setLocalPreview(null);
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled || uploading) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = async () => {
    if (!existing || !onRemove) return;
    setLocalError(null);
    try {
      await onRemove(existing);
      setLocalPreview(null);
    } catch (err) {
      setLocalError(err?.getUserFriendlyMessage?.() || err?.message || 'Could not remove the file.');
    }
  };

  // The slot's own problem (HEIC, too large, upload failed) is more specific
  // than the form-level "upload this document" message.
  const error = localError || externalError;
  const preview = localPreview || existing?.previewUrl || null;
  const isImage = existing ? (existing.mimeType || '').startsWith('image/') : Boolean(localPreview);
  const hasFile = Boolean(existing) || uploading;

  return (
    <div className={className}>
      <div className="mb-1.5 flex items-baseline justify-between">
        <span id={`${id}-label`} className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
        </span>
        {existing && !uploading && (
          <span className="inline-flex items-center gap-1 text-xs text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Uploaded
          </span>
        )}
      </div>

      <div
        role="group"
        aria-labelledby={`${id}-label`}
        data-field={fieldName}
        tabIndex={fieldName ? -1 : undefined}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={[
          'relative rounded-lg border-2 transition-colors',
          hasFile ? 'border-solid border-gray-200 bg-white' : 'border-dashed bg-gray-50',
          isDragging ? 'border-blue-500 bg-blue-50' : (hasFile ? '' : 'border-gray-300'),
          error ? 'border-red-400' : '',
          disabled ? 'opacity-60' : ''
        ].join(' ')}
      >
        {hasFile ? (
          <div className="flex items-center gap-3 p-3">
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-100">
              {uploading && !preview ? (
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" aria-hidden="true" />
              ) : isImage && preview ? (
                <img src={preview} alt="" className="h-full w-full object-cover" />
              ) : (
                <FileText className="h-7 w-7 text-gray-500" aria-hidden="true" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">
                {uploading ? 'Uploading…' : (existing?.originalFileName || 'Document')}
              </p>
              {uploading ? (
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-blue-100" aria-hidden="true">
                  <div className="h-2 rounded-full bg-blue-600 transition-all duration-200" style={{ width: `${Math.max(progress, 5)}%` }} />
                </div>
              ) : (
                <p className="mt-0.5 text-xs text-gray-500">{isImage ? 'Image' : 'PDF'} · tap Replace to upload a different file</p>
              )}
            </div>
            {!uploading && !disabled && (
              <div className="flex flex-shrink-0 flex-col gap-1 sm:flex-row">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Replace
                </button>
                {onRemove && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Remove
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="p-4">
            {isMobile ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-4 text-sm font-medium text-gray-800 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Camera className="h-6 w-6 text-blue-600" aria-hidden="true" />
                  Take photo
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-4 text-sm font-medium text-gray-800 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Upload className="h-6 w-6 text-blue-600" aria-hidden="true" />
                  Choose file
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={disabled}
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full flex-col items-center justify-center gap-2 py-4 text-sm text-gray-600 focus:outline-none"
              >
                <Upload className="h-7 w-7 text-blue-600" aria-hidden="true" />
                <span><span className="font-medium text-blue-700">Choose a file</span> or drag it here</span>
                <span className="text-xs text-gray-500">JPG, PNG or PDF up to 10 MB</span>
              </button>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="sr-only"
          tabIndex={-1}
          onChange={handleInputChange}
          disabled={disabled}
          aria-hidden="true"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture={capture}
          className="sr-only"
          tabIndex={-1}
          onChange={handleInputChange}
          disabled={disabled}
          aria-hidden="true"
        />
      </div>

      {hint && !error && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
};

export default FileSlot;
